// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The player's own audio levels (07 §7).
//
// 07 §7 closes with "every bus has a slider in settings, plus a master volume and a mute-all,
// persisted per account and applied live with no reload". This is that, minus the account — an
// anonymous player has no account and is still entitled to turn the music off, so it lives in
// `localStorage` beside the comfort settings and syncs nowhere.
//
// **Four controls over six buses, and the grouping is the design.** `mixer.ts` splits the mix six
// ways because *mixing rules* need that many; a player does not. Nobody has ever wanted the
// bumpers louder than the drop targets, and a settings page with six rows on it is a page where
// the two rows people actually came for — music and effects — are outnumbered four to two.
//
// So:
//
//   * **`music`** is its own control, because it is the one people tire of first. It is the
//     longest-running sound in the product and the only one a player is likely to be replacing
//     with their own.
//   * **`effects`** is `mechanical` + `game` + `ui` together. All three are "the machine made a
//     noise because something happened", they are never wanted apart, and separating them would
//     let a player silence the coils while the score chimes kept playing — a machine that sounds
//     broken rather than quiet.
//   * **`speech`** is separate from effects **because muting it costs information, not just
//     noise.** The callouts are how the machine says BALL SAVED and which jackpot is lit; a
//     player turning the volume down in a quiet room is making a different decision from one who
//     does not want to be told things. Rolling them together would mean the first player loses
//     the second player's feature without ever choosing to.
//   * **`ambience`** is separate because it is mostly the VR arcade's room bed and is nearly
//     inaudible on a phone. A player who never enters the arcade should not have their effects
//     control quietly governing a bus they have never heard.
//
// **Three named steps rather than a slider**, matching every other control on the settings page
// and the conventions' "prefer selection rather than manual input". A continuous fader is a
// control with a hundred positions, ninety-seven of which nobody can tell apart, and it is the
// one control shape that cannot be operated from a keyboard without a value nobody wants to read
// out loud.
//
// **The level is a trim, not a replacement for the bus's own.** `MixerBus.setVolume` multiplies
// what is set here by `BUS_TRIM_DB`, so `full` is the mix 07 §7 describes rather than unity, and
// a bus does not change character when a player moves it — the compressor is upstream of this
// gain precisely so it cannot.
//
// **Mute-all is `Mixer.setMuted`, which is not "every level to zero".** Writing zeroes would lose
// where the player had put things: unmuting restores whatever the code guessed, and the guess is
// always `full`. The master gain is a separate node for exactly this reason.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'
import { stampPreference } from '../storage/prefStamps'
import type { BusName, Mixer } from './mixer'

/** The four things a player is offered, in the order the settings page lists them. */
export const AUDIO_GROUPS = ['music', 'effects', 'speech', 'ambience'] as const
export type AudioGroup = (typeof AUDIO_GROUPS)[number]

/**
 * Which mixer buses each control owns.
 *
 * Every bus appears exactly once, and there is a test that says so. A bus missing from this table
 * is a bus with no way to turn it down; a bus in two of them is a bus whose level depends on
 * which control was touched last.
 */
export const GROUP_BUSES: Readonly<Record<AudioGroup, readonly BusName[]>> = {
  music: ['music'],
  effects: ['mechanical', 'game', 'ui'],
  speech: ['speech'],
  ambience: ['ambience'],
}

/** Loudest first, because that is the default and a list of choices should open on it. */
export const AUDIO_LEVELS = ['full', 'low', 'off'] as const
export type AudioLevel = (typeof AUDIO_LEVELS)[number]

/**
 * What each step multiplies the bus by.
 *
 * `low` is 0.4, which is about −8 dB — the same distance 07 §7's speech duck moves the music,
 * and chosen for that reason: it is a level the mix is already known to survive, and it is far
 * enough down to be obviously quieter rather than "did that do anything?".
 */
export const LEVEL_VOLUME: Readonly<Record<AudioLevel, number>> = { full: 1, low: 0.4, off: 0 }

/** One key per control, plus the mute. Namespaced like every other preference this client keeps. */
export const AUDIO_LEVEL_KEYS: Readonly<Record<AudioGroup, string>> = {
  music: 'hcc.pinball.audio.music',
  effects: 'hcc.pinball.audio.effects',
  speech: 'hcc.pinball.audio.speech',
  ambience: 'hcc.pinball.audio.ambience',
}

export const AUDIO_MUTED_KEY = 'hcc.pinball.audio.muted'

/**
 * How loud everything is, before the four controls above (07 §7, 06 §5's "master volume").
 *
 * **The mixer has had this since M3-20 and nothing ever called it.** `Mixer.setMasterVolume` is a
 * third input to the master gain, composes correctly with the mute and the pause fade, and is
 * covered by its own unit test — and `applyAudioPrefs` did not set it, no settings control wrote
 * it, and no wrist row offered it. 07 §7 asks for "a slider per bus **plus a master volume** and a
 * mute-all" and 06 §5 names it first in the Sound row, so the requirement had two documents and a
 * working implementation and no way for a player to reach any of it.
 *
 * **A trim over the four, not a fifth group.** The groups answer "which of these do I want less
 * of"; this answers "all of it, quieter", which is a different question and the one somebody asks
 * when a person walks into the room. Folding it into the groups would mean turning the machine
 * down lost where the player had put music against effects.
 *
 * The same three named steps as everything else here, and `off` is deliberately *not* the mute: it
 * silences by gain and the mute silences by state, they compose in `Mixer.gainNow`, and undoing
 * either one alone brings the machine back exactly where it was.
 */
export const AUDIO_MASTER_KEY = 'hcc.pinball.audio.master'

/**
 * Whether callouts are captioned (NFR-022, 06 §5's "callout captions").
 *
 * **Defaults to on, and that is the requirement rather than a preference.** NFR-022 says "every
 * spoken callout is captioned on screen and in the headset", so a player who never touches this
 * gets captions; the control exists because 06 §5 lists one in the Sound row and because a
 * caption is a thing a hearing player may reasonably want out of their way, in the same way
 * subtitles are. Off is a choice, never a default and never a side effect of a mute.
 *
 * **Independent of the `speech` level and of the mute**, deliberately: the whole point of a
 * caption is that it survives a callout nobody can hear, which is the case a deaf player is in
 * permanently and a player in a loud room is in temporarily.
 */
export const AUDIO_CAPTIONS_KEY = 'hcc.pinball.audio.captions'

export interface AudioPrefs {
  /** Mute-all. Independent of the levels, and does not disturb them. */
  readonly muted: boolean
  /** How loud everything is, before the four below. See `AUDIO_MASTER_KEY`. */
  readonly master: AudioLevel
  readonly levels: Readonly<Record<AudioGroup, AudioLevel>>
  /** Whether a spoken callout also appears as text. See `AUDIO_CAPTIONS_KEY`. */
  readonly captions: boolean
}

/**
 * Everything on, which is what a player who has never touched this hears.
 *
 * **The single source of the defaults, and every reader and writer below goes through it.** A
 * default spelled `'full'` at each of the four call sites is four places to change and three
 * places to forget, and the way this fails is silent: one control that no longer starts where the
 * others do, discovered by a player rather than by a test.
 */
export const AUDIO_DEFAULTS: AudioPrefs = {
  muted: false,
  master: 'full',
  levels: { music: 'full', effects: 'full', speech: 'full', ambience: 'full' },
  captions: true,
}

function isLevel(value: string | undefined): value is AudioLevel {
  return value === 'full' || value === 'low' || value === 'off'
}

/** What the player last chose for one control, or `full`. */
export function readAudioLevel(
  group: AudioGroup,
  storage: StorageLike | undefined = safeStorage(),
): AudioLevel {
  const stored = readLocal(storage, AUDIO_LEVEL_KEYS[group])
  return isLevel(stored) ? stored : AUDIO_DEFAULTS.levels[group]
}

/**
 * Store one control's level.
 *
 * `full` removes the row rather than writing the word, the same way `writeFlashingChoice` treats
 * `system`: a player who has never turned anything down has no rows at all, and the settings page
 * that tells them what this browser is keeping stays honest.
 */
export function writeAudioLevel(
  group: AudioGroup,
  level: AudioLevel,
  storage: StorageLike | undefined = safeStorage(),
): void {
  // Stamped whichever branch runs. Going *back* to the default is a choice, and one recorded
  // only as a missing row is one the next sign-in undoes — see `storage/prefStamps.ts`.
  stampPreference(`audio.${group}`, storage)
  if (level === AUDIO_DEFAULTS.levels[group]) {
    removeLocal(storage, AUDIO_LEVEL_KEYS[group])
    return
  }
  writeLocal(storage, AUDIO_LEVEL_KEYS[group], level)
}

/** What the player last chose for the master, or `full`. */
export function readAudioMaster(storage: StorageLike | undefined = safeStorage()): AudioLevel {
  const stored = readLocal(storage, AUDIO_MASTER_KEY)
  return isLevel(stored) ? stored : AUDIO_DEFAULTS.master
}

/** Store the master level. `full` removes the row, like every other control here. */
export function writeAudioMaster(
  level: AudioLevel,
  storage: StorageLike | undefined = safeStorage(),
): void {
  stampPreference('audio.master', storage)
  if (level === AUDIO_DEFAULTS.master) {
    removeLocal(storage, AUDIO_MASTER_KEY)
    return
  }
  writeLocal(storage, AUDIO_MASTER_KEY, level)
}

export function readAudioMuted(storage: StorageLike | undefined = safeStorage()): boolean {
  const stored = readLocal(storage, AUDIO_MUTED_KEY)
  return stored === undefined ? AUDIO_DEFAULTS.muted : stored === '1'
}

/**
 * Store the mute-all.
 *
 * **Persisted, unlike the mute this replaces.** The wrist menu's `sound` row wrote straight to the
 * mixer, so a player who muted a machine in VR, took the headset off and came back was met by a
 * machine at full volume — which is the specific failure that teaches somebody not to trust a
 * mute button.
 */
export function writeAudioMuted(
  muted: boolean,
  storage: StorageLike | undefined = safeStorage(),
): void {
  stampPreference('audio.muted', storage)
  if (muted === AUDIO_DEFAULTS.muted) {
    removeLocal(storage, AUDIO_MUTED_KEY)
    return
  }
  writeLocal(storage, AUDIO_MUTED_KEY, muted ? '1' : '0')
}

export function readAudioCaptions(storage: StorageLike | undefined = safeStorage()): boolean {
  const stored = readLocal(storage, AUDIO_CAPTIONS_KEY)
  return stored === undefined ? AUDIO_DEFAULTS.captions : stored === '1'
}

/** Store whether callouts are captioned. Absent means the default, which is on. */
export function writeAudioCaptions(
  captions: boolean,
  storage: StorageLike | undefined = safeStorage(),
): void {
  stampPreference('audio.captions', storage)
  if (captions === AUDIO_DEFAULTS.captions) {
    removeLocal(storage, AUDIO_CAPTIONS_KEY)
    return
  }
  writeLocal(storage, AUDIO_CAPTIONS_KEY, captions ? '1' : '0')
}

/** Everything at once, which is what the mixer wants at construction. */
export function readAudioPrefs(storage: StorageLike | undefined = safeStorage()): AudioPrefs {
  return {
    muted: readAudioMuted(storage),
    master: readAudioMaster(storage),
    levels: {
      music: readAudioLevel('music', storage),
      effects: readAudioLevel('effects', storage),
      speech: readAudioLevel('speech', storage),
      ambience: readAudioLevel('ambience', storage),
    },
    captions: readAudioCaptions(storage),
  }
}

/**
 * Put the preferences on a mixer.
 *
 * Called once when the mixer is built and again whenever a control moves, and it is deliberately
 * a whole-state apply rather than a delta: the mixer is the thing that can be out of step, and a
 * function that only knows how to change one bus cannot fix a mixer that was built before the
 * player's choice was readable.
 *
 * Every write goes through `setVolume`, which glides. A gain assigned mid-signal is a step
 * discontinuity, which is a click — audible on the master mute, which is the one control here
 * likely to be pressed while a coil is firing.
 */
export function applyAudioPrefs(mixer: Mixer, prefs: AudioPrefs): void {
  for (const group of AUDIO_GROUPS) {
    const volume = LEVEL_VOLUME[prefs.levels[group]]
    for (const bus of GROUP_BUSES[group]) mixer.bus(bus).setVolume(volume)
  }
  // **The line that was missing**, and the reason `setMasterVolume` had no caller in the product.
  // Independent of the mute rather than sequenced with it: `Mixer.gainNow` combines them, which is
  // what makes muted-then-quieter-then-unmuted land exactly where the player left it.
  mixer.setMasterVolume(LEVEL_VOLUME[prefs.master])
  mixer.setMuted(prefs.muted)
}

/**
 * What a mute control is called, given what it is doing.
 *
 * **States the condition, never the action** — M8-09's rule, beside `micLabel` and `voiceLabel`
 * which follow it for the same reason: "Mute" on a control that is already muted is how a mute
 * control comes to lie. The player reads it, believes it describes what is happening, and turns
 * the machine up in a quiet room.
 *
 * Here rather than in `voiceMute.ts` because this is the machine's own sound and that file is
 * about people, and the two must not end up muting each other by accident.
 */
export function soundLabel(muted: boolean): string {
  return muted ? 'Sound off' : 'Sound on'
}

/**
 * What a captions control is called, given what it is doing.
 *
 * States the condition rather than the action, beside `soundLabel` and for its reason: "Captions"
 * on a control that is already captioning says nothing, and "Turn captions off" on a row that is
 * *showing* the state is the lie M8-09 banned.
 */
export function captionsLabel(captions: boolean): string {
  return captions ? 'Captions on' : 'Captions off'
}

/**
 * What the master control is called, given where it is.
 *
 * States the condition, beside `soundLabel` and `captionsLabel` and for their reason. The word is
 * "Volume" rather than "Master" because a player is not reading a mixing desk: the row above says
 * whether there is any sound at all, and this one says how much.
 *
 * `low` reads "quieter" rather than "low" so each step is a state a player can check themselves
 * against — "Volume quieter" describes the machine, "Volume low" names a setting. The settings
 * page already uses that word for that step, so the two surfaces say the same thing.
 */
export function masterLabel(level: AudioLevel): string {
  if (level === 'full') return 'Volume full'
  return level === 'low' ? 'Volume quieter' : 'Volume off'
}

/**
 * The step a press of the master row moves to, wrapping at the end.
 *
 * A wrist row is one button, so the three steps have to be a cycle; the settings page, which has
 * room for three buttons, sets them directly. Loudest first, in `AUDIO_LEVELS` order, so the
 * first press of a machine at full volume makes it quieter rather than silent.
 */
export function nextAudioLevel(level: AudioLevel): AudioLevel {
  // Derived from `AUDIO_LEVELS` rather than written out as a ternary chain, so a fourth step would
  // join the cycle instead of being silently skipped by it. The `??` is unreachable — the modulus
  // is inside the array — and is a value rather than a `!` assertion for that reason: the default
  // is where a level with no successor should land anyway.
  return AUDIO_LEVELS[(AUDIO_LEVELS.indexOf(level) + 1) % AUDIO_LEVELS.length] ?? AUDIO_DEFAULTS.master
}

/**
 * The same preferences with one control moved. Kept here so no caller rebuilds the shape.
 *
 * **Spreading rather than listing the fields is the whole point.** This function is the single
 * place that knows the shape, and the first version of it enumerated `muted` and `levels`
 * explicitly — so adding `captions` to the interface would have compiled, and silently reset a
 * player's captions to the default every time they moved a volume control.
 */
export function withAudioLevel(prefs: AudioPrefs, group: AudioGroup, level: AudioLevel): AudioPrefs {
  return { ...prefs, levels: { ...prefs.levels, [group]: level } }
}

/** The same preferences with the master moved. */
export function withAudioMaster(prefs: AudioPrefs, master: AudioLevel): AudioPrefs {
  return { ...prefs, master }
}

/** The same preferences with the captions control moved. */
export function withAudioCaptions(prefs: AudioPrefs, captions: boolean): AudioPrefs {
  return { ...prefs, captions }
}

/**
 * The same preferences with the mute moved.
 *
 * **Missing until captions arrived, which is why two callers hand-rebuilt the shape** — the
 * runtime's `setAudioMuted` and the settings page's `chooseMuted` both wrote
 * `{ muted, levels: prefs.levels }`, and both would have dropped a player's captions on the floor
 * the first time they pressed mute. The type checker caught them, and the reason it could is that
 * this file owns the shape; the fix is the helper that should have existed beside `withAudioLevel`
 * from the start rather than a third literal.
 */
export function withAudioMuted(prefs: AudioPrefs, muted: boolean): AudioPrefs {
  return { ...prefs, muted }
}
